import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { MessageKey } from "@wato/i18n";
import { useI18n } from "@wato/i18n/react";
import { useApi } from "../../api/context";
import { isApiError } from "../../api/errors";
import type { AssistantAction } from "../../api/owner-assistant-types";
import { useWorkspace } from "../workspace/WorkspaceContext";
import { isAssistantShortcut } from "./suggested-questions";
import { useAssistantUnread } from "./useAssistantUnread";

/** OWN-317: the panel's chat plus the three console sections that moved in from /console. */
export type AssistantTab = "chat" | "summaries" | "questions" | "proposals";

const key = (suffix: string) => `dashboard.ownerAssistant.${suffix}` as MessageKey;

export type AssistantTurn =
  | { id: number; role: "user"; text: string }
  | { id: number; role: "assistant"; text: string; actions: AssistantAction[] };

export type ActionState =
  | { status: "running" }
  | { status: "done"; href?: string }
  | { status: "cancelled" }
  | { status: "error"; code: string };

const ACTION_ERROR_CODES = [
  "CONVERSATION_ALREADY_ASSIGNED",
  "NO_CHANNEL",
  "FORBIDDEN",
  "INVALID_INPUT",
] as const;
export function actionErrorKey(code: string): MessageKey {
  return key(
    `action.errors.${(ACTION_ERROR_CODES as readonly string[]).includes(code) ? code : "generic"}`,
  );
}

/** Backend returns `{error:{code}}` only: map the code (and status) to a chat message key. */
export function chatErrorKey(e: unknown): MessageKey {
  if (!isApiError(e)) return key("errorGeneric");
  const code = e.code;
  if (e.status === 429 || code === "RATE_LIMITED" || /RATE/.test(code))
    return key("errorRateLimited");
  if (e.status === 402 || code === "AI_BUDGET_EXCEEDED" || /BUDGET|QUOTA|LIMIT/.test(code))
    return key("errorBudget");
  if (e.status === 403 || code === "FORBIDDEN") return key("errorForbidden");
  return key("errorGeneric");
}

export interface OwnerAssistantContextValue {
  readonly open: boolean;
  readonly setOpen: (open: boolean) => void;
  readonly tab: AssistantTab;
  readonly setTab: (tab: AssistantTab) => void;
  /** Unread console messages (summaries, questions, proposals); drives the FAB dot. */
  readonly unread: number;
  readonly clearUnread: () => void;
  /** Opens the panel and sends `question` as a new user message. */
  readonly ask: (question: string) => void;
  readonly turns: readonly AssistantTurn[];
  readonly busy: boolean;
  readonly error: string | null;
  readonly actionStates: Readonly<Record<string, ActionState>>;
  readonly runAction: (action: AssistantAction) => Promise<void>;
  readonly cancelAction: (id: string) => void;
}

const OwnerAssistantContext = createContext<OwnerAssistantContextValue | null>(null);

export function useOwnerAssistant(): OwnerAssistantContextValue {
  const value = useContext(OwnerAssistantContext);
  if (!value) throw new Error("useOwnerAssistant must be used inside <OwnerAssistantProvider>");
  return value;
}

/** Null outside the provider (e.g. cards rendered in isolation in tests). */
export function useOptionalOwnerAssistant(): OwnerAssistantContextValue | null {
  return useContext(OwnerAssistantContext);
}

/**
 * OWN-163: owns the assistant chat so closing/reopening the panel keeps the conversation.
 * The assistant only PROPOSES actions; `runAction` is called solely from an explicit «Confirm».
 */
export function OwnerAssistantProvider({ children }: { children: ReactNode }) {
  const { t, locale } = useI18n();
  const api = useApi();
  const { workspace } = useWorkspace();
  const workspaceId = workspace.id;
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<AssistantTab>("chat");
  const { count: unread, clear: clearUnread } = useAssistantUnread();
  const [turns, setTurns] = useState<AssistantTurn[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionStates, setActionStates] = useState<Record<string, ActionState>>({});
  const nextId = useRef(1);
  const turnsRef = useRef<AssistantTurn[]>([]);
  const busyRef = useRef(false);
  /** Bumped on every workspace change; async results from an older epoch are dropped. */
  const epochRef = useRef(0);
  turnsRef.current = turns;

  // A different workspace is a different conversation.
  useEffect(() => {
    epochRef.current += 1;
    busyRef.current = false;
    setBusy(false);
    setTurns([]);
    setError(null);
    setActionStates({});
  }, [workspaceId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (isAssistantShortcut(event)) {
        event.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const send = useCallback(
    async (question: string) => {
      const text = question.trim();
      if (!text || busyRef.current) return;
      const history = turnsRef.current
        .filter((turn) => turn.text !== "")
        .slice(-10)
        .map((turn) => ({ role: turn.role, content: turn.text }));
      const epoch = epochRef.current;
      busyRef.current = true;
      setBusy(true);
      setError(null);
      setTurns((prev) => [...prev, { id: nextId.current++, role: "user", text }]);
      try {
        const res = await api.sendOwnerAssistantMessage(workspaceId, { text, locale, history });
        if (epoch !== epochRef.current) return;
        setTurns((prev) => [
          ...prev,
          {
            id: nextId.current++,
            role: "assistant",
            text: res.reply,
            actions: Array.isArray(res.actions) ? res.actions : [],
          },
        ]);
      } catch (e) {
        if (epoch !== epochRef.current) return;
        setError(t(chatErrorKey(e)));
      } finally {
        if (epoch === epochRef.current) {
          busyRef.current = false;
          setBusy(false);
        }
      }
    },
    [api, locale, t, workspaceId],
  );

  const ask = useCallback(
    (question: string) => {
      setOpen(true);
      setTab("chat");
      void send(question);
    },
    [send],
  );

  const runAction = useCallback(
    async (action: AssistantAction) => {
      const epoch = epochRef.current;
      setActionStates((prev) => ({ ...prev, [action.id]: { status: "running" } }));
      try {
        const res = await api.runOwnerAssistantAction(workspaceId, {
          kind: action.kind,
          params: action.params,
        });
        if (epoch !== epochRef.current) return;
        setActionStates((prev) => ({
          ...prev,
          [action.id]: { status: "done", ...(res.href ? { href: res.href } : {}) },
        }));
      } catch (e) {
        if (epoch !== epochRef.current) return;
        setActionStates((prev) => ({
          ...prev,
          [action.id]: { status: "error", code: isApiError(e) ? e.code : "" },
        }));
      }
    },
    [api, workspaceId],
  );

  const cancelAction = useCallback((id: string) => {
    setActionStates((prev) => ({ ...prev, [id]: { status: "cancelled" } }));
  }, []);

  const value = useMemo<OwnerAssistantContextValue>(
    () => ({
      open,
      setOpen,
      tab,
      setTab,
      unread,
      clearUnread,
      ask,
      turns,
      busy,
      error,
      actionStates,
      runAction,
      cancelAction,
    }),
    [
      open,
      tab,
      unread,
      clearUnread,
      ask,
      turns,
      busy,
      error,
      actionStates,
      runAction,
      cancelAction,
    ],
  );
  return <OwnerAssistantContext.Provider value={value}>{children}</OwnerAssistantContext.Provider>;
}
